import { getSoundSettings, onSoundSettings } from './sounds.js';

// Radio (decision 2026-09-29): an Icecast stream played straight from the stream server with an
// <audio> element – not through Areena or the Cloudflare tunnel. Only players with a music
// permission get the stream address. The station broadcasts only now and then: while it is off
// the air the stream answers 404, and the player tries again every minute. Playback continues
// across the pages of the application.

export interface RadioConfig {
  name: string;
  url: string;
}

/**
 * off: switched off or no permission · waiting: tap needed (browsers start audio only after a
 * user gesture) · connecting · playing · offline: not broadcasting, retrying · popup: playing in the
 * separate radio window (user request 2026-09-29), this window stays quiet.
 */
export type RadioStatus = 'off' | 'waiting' | 'connecting' | 'playing' | 'offline' | 'popup';

const RETRY_MS = 60_000;
/** The radio window says it is open every 2 s; after this long without a word it is gone. */
const POPUP_TIMEOUT_MS = 6_000;
export const RADIO_CHANNEL = 'areena-radio';

let popupSeen = 0;
const popupOpen = () => Date.now() - popupSeen < POPUP_TIMEOUT_MS;

let config: RadioConfig | null = null;
let status: RadioStatus = 'off';
let element: HTMLAudioElement | null = null;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let unlocked = false;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());
function setStatus(next: RadioStatus) {
  if (status === next) return;
  status = next;
  notify();
}

function audio(): HTMLAudioElement {
  if (!element) {
    element = new Audio();
    element.preload = 'none';
    element.addEventListener('playing', () => setStatus('playing'));
    element.addEventListener('waiting', () => status === 'playing' && setStatus('connecting'));
    // Not on the air (404) or the broadcast ended: try again later.
    element.addEventListener('error', () => element?.getAttribute('src') && offline());
    element.addEventListener('ended', () => element?.getAttribute('src') && offline());
  }
  return element;
}

function release() {
  clearTimeout(retryTimer);
  if (!element) return;
  element.pause();
  // Drop the connection: a live stream is not buffered while paused.
  element.removeAttribute('src');
  element.load();
}

function offline() {
  if (!config || !getSoundSettings().music) return;
  release();
  setStatus('offline');
  retryTimer = setTimeout(start, RETRY_MS);
}

function start() {
  clearTimeout(retryTimer);
  if (!config || !getSoundSettings().music) {
    release();
    setStatus('off');
    return;
  }
  if (popupOpen()) {
    release();
    setStatus('popup');
    return;
  }
  if (!unlocked) {
    setStatus('waiting');
    return;
  }
  const a = audio();
  a.volume = getSoundSettings().musicVolume;
  a.muted = getSoundSettings().musicMuted;
  // A fresh address each time, so that no old buffered part of the stream is played.
  a.src = `${config.url}${config.url.includes('?') ? '&' : '?'}t=${Date.now()}`;
  setStatus('connecting');
  a.play().catch((err: unknown) => {
    if ((err as { name?: string }).name === 'NotAllowedError') {
      unlocked = false;
      release();
      setStatus('waiting');
    } else offline();
  });
}

/** The radio of this player (null: no permission, logged out). */
export function setRadioConfig(next: RadioConfig | null): void {
  const changed = next?.url !== config?.url || next?.name !== config?.name;
  config = next;
  if (changed) start();
  notify();
}

export function radioConfig(): RadioConfig | null {
  return config;
}

export function radioStatus(): RadioStatus {
  return status;
}

export function onRadio(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Switching the radio on/off and the volume come from the sound settings (stored per browser).
let wasOn = getSoundSettings().music;
onSoundSettings((s) => {
  if (element) {
    element.volume = s.musicVolume;
    element.muted = s.musicMuted;
  }
  if (s.music !== wasOn) {
    wasOn = s.music;
    start();
  }
});

/** Browsers allow audio only after a user gesture: the radio starts on the first one. */
export function installRadioUnlock(): () => void {
  const events = ['touchend', 'click', 'keydown'] as const;
  const handler = () => {
    if (unlocked) return;
    unlocked = true;
    if (status === 'waiting') start();
  };
  events.forEach((e) => addEventListener(e, handler, { passive: true }));
  return () => events.forEach((e) => removeEventListener(e, handler));
}

// The separate radio window (radio.html, user request 2026-09-29) keeps playing when the Areena
// window is closed. While it is open this window is quiet; when it closes, the radio continues here.
const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(RADIO_CHANNEL);
if (channel) {
  channel.onmessage = (e: MessageEvent<{ type: string }>) => {
    if (e.data?.type === 'alive') {
      const wasOpen = popupOpen();
      popupSeen = Date.now();
      if (!wasOpen || status !== 'popup') start();
    } else if (e.data?.type === 'closed') {
      popupSeen = 0;
      start();
    }
  };
  // The window may also disappear without saying goodbye (crash, mobile browser).
  setInterval(() => {
    if (status === 'popup' && !popupOpen()) start();
  }, 3_000);
}

export function openRadioPopup(): void {
  const win = open('radio.html', RADIO_CHANNEL, 'popup,width=380,height=300');
  if (!win) return; // blocked by the browser: keep playing here
  popupSeen = Date.now();
  start();
}

export const radioPopupSupported = channel !== null;
